import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { DLG_BTN_PRIMARY, DLG_BTN_SECONDARY } from '@/components/ui/dialogButtons';

import {
  useConfiguracionNotificaciones,
  type ConfiguracionNotificaciones,
} from '@/components/hooks/useConfiguracionNotificaciones';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  perfil: 'empresa' | 'trabajador';
}

type Campo = keyof ConfiguracionNotificaciones;

// Qué toggles ve cada perfil, y con qué texto. El orden aquí es el orden
// en que se pintan.
const OPCIONES: Record<Campo, { label: string; desc: string; perfiles: Props['perfil'][] }> = {
  notifica_cita_asignada: {
    label: 'Cita asignada',
    desc: 'Te avisa cuando te asignan una nueva cita.',
    perfiles: ['empresa', 'trabajador'],
  },
  notifica_cita_trabajador_asignada: {
    label: 'Cita asignada a un trabajador',
    desc: 'Te avisa cuando uno de tus trabajadores recibe una nueva cita.',
    perfiles: ['empresa'],
  },
  notifica_cita_cancelada: {
    label: 'Cita cancelada',
    desc: 'Te avisa cuando se cancela una cita que tienes asignada.',
    perfiles: ['empresa', 'trabajador'],
  },
  notifica_cita_trabajador_cancelada: {
    label: 'Cita cancelada de un trabajador',
    desc: 'Te avisa cuando se cancela una cita de uno de tus trabajadores.',
    perfiles: ['empresa'],
  },
  notifica_cita_reprogramada: {
    label: 'Cita reprogramada',
    desc: 'Te avisa cuando cambian la fecha o la hora de una cita que tienes asignada.',
    perfiles: ['empresa', 'trabajador'],
  },
  notifica_cita_trabajador_reprogramada: {
    label: 'Cita reprogramada de un trabajador',
    desc: 'Te avisa cuando cambian la fecha o la hora de una cita de uno de tus trabajadores.',
    perfiles: ['empresa'],
  },
  notifica_bono_comprado: {
    label: 'Bono comprado',
    desc: 'Te avisa cuando un cliente compra un bono.',
    perfiles: ['empresa'],
  },
  notifica_ausencia_solicitada: {
    label: 'Ausencia solicitada',
    desc: 'Te avisa cuando un trabajador pide una ausencia.',
    perfiles: ['empresa'],
  },
  notifica_ausencia_respondida: {
    label: 'Ausencia respondida',
    desc: 'Te avisa cuando aprueban o rechazan tu solicitud de ausencia.',
    perfiles: ['trabajador'],
  },
};

export default function NotificationSettingsDialog({ open, onOpenChange, perfil }: Props) {
  const { config, loading, saving, updateAll } = useConfiguracionNotificaciones();

  // Estado local: los switches tocan esto, no la BD. Solo se persiste al
  // pulsar "Guardar". "Cerrar" descarta cualquier cambio sin guardar.
  const [localConfig, setLocalConfig] = useState<ConfiguracionNotificaciones>(config);

  useEffect(() => {
    if (open && !loading) {
      setLocalConfig(config);
    }
  }, [open, loading, config]);

  const campos = (Object.keys(OPCIONES) as Campo[]).filter((campo) =>
    OPCIONES[campo].perfiles.includes(perfil)
  );

  const handleCerrar = () => {
    setLocalConfig(config); // descarta cambios no guardados
    onOpenChange(false);
  };

  const handleGuardar = async () => {
    const cambios: Partial<ConfiguracionNotificaciones> = {};
    for (const campo of campos) {
      if (localConfig[campo] !== config[campo]) {
        cambios[campo] = localConfig[campo];
      }
    }

    if (Object.keys(cambios).length === 0) {
      onOpenChange(false);
      return;
    }

    const resp = await updateAll(cambios);
    if (!resp.ok) {
      toast.error('No se pudieron guardar las preferencias');
      return;
    }

    toast.success('Preferencias guardadas');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : handleCerrar())}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >
        <div className="p-5 sm:p-6 pb-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-[#103254] dark:text-white">
              <Bell className="w-5 h-5 text-[#288E7F]" />
              Notificaciones
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              Elige qué avisos quieres recibir en el panel y en el navegador.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 space-y-3 flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
              Cargando…
            </div>
          ) : (
            campos.map((campo) => {
              const { label, desc } = OPCIONES[campo];
              return (
                <div
                  key={campo}
                  className="flex items-center justify-between gap-3 border border-gray-200 dark:border-white/10 rounded-lg px-4 py-3 bg-white dark:bg-white/5 hover:shadow-sm transition-shadow"
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {label}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{desc}</span>
                  </div>
                  <Switch
                    checked={localConfig[campo]}
                    disabled={saving}
                    onCheckedChange={(v) =>
                      setLocalConfig((prev) => ({ ...prev, [campo]: v }))
                    }
                  />
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="p-5 sm:p-6 pt-4 flex-row gap-3">
          <Button
            variant="outline"
            className={`flex-1 ${DLG_BTN_SECONDARY}`}
            onClick={handleCerrar}
            disabled={saving}
          >
            Cerrar
          </Button>
          <Button
            className={`flex-1 ${DLG_BTN_PRIMARY}`}
            onClick={handleGuardar}
            disabled={saving || loading}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}